/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 * 
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { fetchBase, FetchError, FetchSuccess } from "./managers";

const loginUser = async (username, password) => {
  try {
    localStorage.clear();
    const res = await fetchBase(`user-dashboard/login`, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res, { redirectOnUnauthorized: false });
  }
};

// Contract M2: POST /api/v3/user-dashboard/reset-password — unauthenticated.
// 200 carries a generic message for every permanent outcome (unknown, blank or
// malformed email, account with no sendable event), so account existence can
// never be inferred and the client must not branch UI on it. BE generates a new
// temporary password, invalidates the old one immediately (all devices), and
// emails it. Signing in with it returns the /login step response
// (is_password_reset_required: true).
// 422 { errors } is the retryable case (e.g. email delivery failed) — surface
// the message and let the user resubmit; it does NOT mean the address exists.
// alertError off: FetchError would prefix the payload key onto the toast
// ("Errors We could not…"), so the page surfaces the message itself.
export const forgotPassword = async (email) => {
  try {
    const res = await fetchBase(
      `user-dashboard/reset-password`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
      { version: "v3" },
    );
    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res, {
      redirectOnUnauthorized: false,
      alertError: false,
    });
  }
};

// Contract M1: POST /api/v3/user-dashboard/create-new-password — authorized by
// the short-lived STEP token from the /login or /magic-link 201 step response
// (30-minute lifetime, single-use), NOT the session token. Both fields carry
// the same lowercase sha256 hex of the plaintext (server enforces
// /\A[a-f0-9]{64}\z/). 200 returns a full login payload (session token,
// user_events, ...) — the client swaps tokens and proceeds without another
// login; all other sessions are terminated. 401 ({errors, is_expired: true})
// means the step token expired or was already used → back to Login.
export const createNewPassword = async (stepToken, hashedPassword) => {
  try {
    const res = await fetchBase(
      `user-dashboard/create-new-password`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${stepToken}` },
        body: JSON.stringify({
          password: hashedPassword,
          password_confirmation: hashedPassword,
        }),
      },
      { version: "v3" },
    );
    return new FetchSuccess(res);
  } catch (res) {
    // alertError off: the page surfaces 422s inline and toasts the 401 itself
    // — FetchError toasting too would double every message.
    throw new FetchError(res, {
      redirectOnUnauthorized: false,
      alertError: false,
    });
  }
};

export const loginMagicLink = async (token) => {
  try {
    // I commented this line to fix magic link with sign up
    // localStorage.clear();
    const response = await fetchBase(`user-dashboard/magic-link?lt=${token}`, {
      method: "POST",
    });
    return new FetchSuccess(response);
  } catch (res) {
    throw new FetchError(res, { message: "The link has expired. Please log in with your credentials" });
  }
};

export default loginUser;
